import { TBankAccount } from '../types'
import { Menu } from 'antd'
import Typography from 'components/Typography/Typography'
import { ThreeDotsMenu } from 'components/ThreeDotsMenu'
import { BankAccountsTableMenuItemsOptions } from './constants'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getUser } from 'redux/store/user/getters'
import {
  ACTIONS,
  SUBJECTS,
  bankAccountSubject,
  defineAbility
} from '../Abilities'

export const BankAccountsTableMenu = ({
  row,
  onSendDeposit,
  onDeleteBankAccount,
  onVerifyDeposit,
  onSetAsDefaultReceivableAccount
}: {
  row: TBankAccount
  onSendDeposit: (id: string) => void
  onDeleteBankAccount: (row: TBankAccount) => void
  onVerifyDeposit: (row: TBankAccount) => void
  onSetAsDefaultReceivableAccount: (row: TBankAccount) => void
}) => {
  const user = useSelector(getUser)
  const ability = useMemo(() => defineAbility(user), [user])
  const canSendDeposit = useMemo(
    () => ability.can(ACTIONS.SEND, bankAccountSubject(SUBJECTS.DEPOSIT, row)),
    [ability, row]
  )

  const canVerifyDeposit = useMemo(
    () =>
      ability.can(ACTIONS.VERIFY, bankAccountSubject(SUBJECTS.DEPOSIT, row)),
    [ability, row]
  )

  const canResendDeposit = useMemo(
    () =>
      ability.can(ACTIONS.RESEND, bankAccountSubject(SUBJECTS.DEPOSIT, row)),
    [ability, row]
  )

  const canSetAsDefaultReceivableAccount = useMemo(
    () =>
      ability.can(
        ACTIONS.SET_AS_DEFAULT,
        bankAccountSubject(SUBJECTS.BANK_ACCOUNT_DETAILS, row)
      ),
    [ability, row]
  )

  return (
    <ThreeDotsMenu className="actual-saving-menu">
      <BankAccountsTableMenuItems
        row={row}
        onSendDeposit={onSendDeposit}
        canSendDeposit={canSendDeposit}
        canResendDeposit={canResendDeposit}
        canSetAsDefaultReceivableAccount={canSetAsDefaultReceivableAccount}
        canVerifyDeposit={canVerifyDeposit}
        onDeleteBankAccount={onDeleteBankAccount}
        onVerifyDeposit={onVerifyDeposit}
        onSetAsDefaultReceivableAccount={onSetAsDefaultReceivableAccount}
      />
    </ThreeDotsMenu>
  )
}

export const BankAccountsTableMenuItems = ({
  row,
  onSendDeposit,
  canSendDeposit,
  canResendDeposit,
  canSetAsDefaultReceivableAccount,
  canVerifyDeposit,
  onDeleteBankAccount,
  onVerifyDeposit,
  onSetAsDefaultReceivableAccount
}: {
  row: TBankAccount
  onSendDeposit: (id: string) => void
  onDeleteBankAccount: (row: TBankAccount) => void
  onVerifyDeposit: (row: TBankAccount) => void
  onSetAsDefaultReceivableAccount: (row: TBankAccount) => void
  canSendDeposit: boolean
  canResendDeposit: boolean
  canVerifyDeposit: boolean
  canSetAsDefaultReceivableAccount: boolean
}) => {
  const handleClick = (key: string) => {
    switch (key) {
      case 'send_deposit':
      case 'resend_deposit': {
        onSendDeposit(row.uuid)
        return
      }
      case 'verify_deposit': {
        onVerifyDeposit(row)
        return
      }
      case 'set_as_default_receivable_account': {
        onSetAsDefaultReceivableAccount(row)
        return
      }
      case 'delete':
        onDeleteBankAccount(row)
        return
      default:
        return
    }
  }
  return (
    <Menu className="menu-items-wrapper actual-savings-menu" selectedKeys={[]}>
      {BankAccountsTableMenuItemsOptions.map((i) => {
        if (i.key === 'send_deposit' && !canSendDeposit) return null
        if (i.key === 'resend_deposit' && !canResendDeposit) return null
        if (i.key === 'verify_deposit' && !canVerifyDeposit) return null
        if (
          i.key === 'set_as_default_receivable_account' &&
          (!canSetAsDefaultReceivableAccount || row.is_default)
        )
          return null
        return (
          <Menu.Item key={i.key} onClick={() => handleClick(i.key)}>
            <Typography.Body1>{i.label}</Typography.Body1>
          </Menu.Item>
        )
      })}
    </Menu>
  )
}
